/**
 * Buy Button Anywhere — PDP component.
 * Submits selected variant(s) to /cart/add.js, then either opens the theme's
 * quick-cart drawer, redirects to /cart, or redirects to /checkout.
 *
 * Binds globally via event delegation so it works for any [data-bba-form]
 * present at load OR added later (theme editor re-render, AJAX).
 */
(function () {
  'use strict';

  const SELECTOR_FORM = 'form[data-bba-form]';
  const SELECTOR_VARIANT_INPUT = '[data-bba-variant-input]';

  function setLoading(btn, isLoading) {
    if (!btn) return;
    if (isLoading) {
      btn.dataset.bbaLoading = 'true';
      btn.setAttribute('disabled', 'disabled');
      const label = btn.querySelector('.bba-button__label');
      if (label) label.style.opacity = '0.6';
    } else {
      delete btn.dataset.bbaLoading;
      if (btn.getAttribute('aria-disabled') !== 'true') btn.removeAttribute('disabled');
      const label = btn.querySelector('.bba-button__label');
      if (label) label.style.opacity = '';
    }
  }

  function openDrawer() {
    // 2026-05-12 — drawer-everywhere policy. Never fall through to /cart from
    // the BBA. If the cart icon isn't on the page (rare DOM-missing case),
    // dispatch the canonical quick-cart:open event so the theme's quick-cart
    // listener still opens the drawer. The /cart page is reserved for direct
    // URL nav, Klaviyo email links, and the last-resort no-JS fallback.
    const cartIcon = document.querySelector('[data-js-cart-icon]');
    if (cartIcon) {
      cartIcon.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      return;
    }
    try {
      window.dispatchEvent(new CustomEvent('quick-cart:open'));
    } catch (e) {
      document.dispatchEvent(new CustomEvent('quick-cart:open'));
    }
  }

  /**
   * Refresh the quick-cart drawer's contents by re-fetching its section HTML
   * and surgically updating only the inner sub-elements (items list, cart
   * count, subtotal, discounts) — leaving the .quick-cart wrapper and all
   * its bound event listeners / theme-class references intact.
   *
   * Mirrors the theme's own refreshQuickCart() in theme.js.
   */
  async function refreshQuickCart() {
    const quickCartEl = document.querySelector('.quick-cart');
    if (!quickCartEl) return;
    const wrapper = quickCartEl.closest('[id^="shopify-section-"]');
    if (!wrapper) return;
    const sectionId = wrapper.id.replace('shopify-section-', '');

    try {
      const res = await fetch('/cart?section_id=' + encodeURIComponent(sectionId));
      if (!res.ok) return;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const innerSelectors = [
        '.quick-cart__wrapper .quick-cart__items',
        '.quick-cart__wrapper .quick-cart__heading sup',
        '.quick-cart__wrapper .quick-cart__footer-subtotal span',
        '.quick-cart__wrapper .quick-cart__item-discounts',
      ];

      innerSelectors.forEach((sel) => {
        const fresh = doc.querySelector(sel);
        const live = document.querySelector(sel);
        if (fresh && live) live.innerHTML = fresh.innerHTML;
      });

      // If cart toggled between empty <-> has-items, the items list element
      // may not exist on one side. Fall back to a container-level swap.
      const liveItems = document.querySelector('.quick-cart__wrapper .quick-cart__items');
      const freshItems = doc.querySelector('.quick-cart__wrapper .quick-cart__items');
      if (!liveItems || !freshItems) {
        const liveInner = document.querySelector('.quick-cart__container');
        const freshInner = doc.querySelector('.quick-cart__container');
        if (liveInner && freshInner) {
          liveInner.innerHTML = freshInner.innerHTML;
        }
      }
    } catch (err) {
      console.error('[bba] quick-cart refresh failed:', err);
    }
  }

  function notifyThemeCartUpdated(cartItem) {
    document.dispatchEvent(new CustomEvent('apps:product-added-to-cart', { detail: { item: cartItem } }));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item: cartItem } }));
  }

  /**
   * Read the live quantity the user has typed into the PDP's quantity selector,
   * falling back to the block's configured quantity if the selector isn't on the page.
   */
  function resolveQuantity(form) {
    const productId = form.getAttribute('data-bba-product-id');
    const source = form.getAttribute('data-bba-source');

    if (source === 'current' && productId) {
      const pdpInput =
        document.getElementById('Quantity-Input-' + productId) ||
        document.getElementById('Quantity-' + productId) ||
        document.querySelector('[data-quantity-input]');
      if (pdpInput && pdpInput.value) {
        const n = parseInt(pdpInput.value, 10);
        if (!isNaN(n) && n > 0) return n;
      }
    }

    const hidden = form.querySelector('input[name="quantity"]');
    const fallback = parseInt(hidden && hidden.value, 10);
    return Math.max(1, !isNaN(fallback) ? fallback : 1);
  }

  function collectItems(form) {
    const ids = Array.from(form.querySelectorAll('input[name="id"]'))
      .map((i) => parseInt(i.value, 10))
      .filter((n) => !isNaN(n) && n > 0);
    const qty = resolveQuantity(form);
    return ids.map((id) => ({ id, quantity: qty }));
  }

  function showError(form, message) {
    let err = form.querySelector('[data-bba-error]');
    if (!err) {
      err = document.createElement('div');
      err.setAttribute('data-bba-error', '');
      err.className = 'bba-error';
      err.setAttribute('role', 'alert');
      form.appendChild(err);
    }
    err.textContent = message;
  }

  function clearError(form) {
    const err = form.querySelector('[data-bba-error]');
    if (err) err.textContent = '';
  }

  async function handleSubmit(event) {
    const form = event.target.closest(SELECTOR_FORM);
    if (!form) return;
    event.preventDefault();

    const btn = form.querySelector('[data-bba-submit]');
    if (btn && btn.dataset.bbaLoading === 'true') return;

    const items = collectItems(form);
    if (items.length === 0) {
      showError(form, 'No variant selected.');
      return;
    }

    clearError(form);
    setLoading(btn, true);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (!response.ok) {
        const msg = (data && (data.description || data.message)) || 'Could not add to cart.';
        throw new Error(msg);
      }

      const firstItem = Array.isArray(data.items) ? data.items[0] : data;
      notifyThemeCartUpdated(firstItem);

      const action = form.getAttribute('data-bba-action') || 'drawer';
      if (action === 'cart') {
        window.location.href = '/cart';
      } else if (action === 'checkout') {
        window.location.href = '/checkout';
      } else {
        await refreshQuickCart();
        openDrawer();
        setLoading(btn, false);
      }
    } catch (err) {
      console.error('[bba] add to cart failed:', err);
      showError(form, err.message || 'Could not add to cart.');
      setLoading(btn, false);
    }
  }

  function syncCurrentVariantForms(variant) {
    if (!variant) return;
    const forms = document.querySelectorAll(SELECTOR_FORM + '[data-bba-source="current"]');
    forms.forEach((form) => {
      const input = form.querySelector(SELECTOR_VARIANT_INPUT);
      if (input) input.value = variant.id;
      const btn = form.querySelector('[data-bba-submit]');
      if (!btn) return;
      const label = btn.querySelector('.bba-button__label');
      if (variant.available === false) {
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-disabled', 'true');
        if (label) label.textContent = btn.dataset.bbaSoldoutLabel || 'Sold out';
      } else {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
        if (label) label.textContent = btn.dataset.bbaLabel || label.textContent;
      }
    });
  }

  document.addEventListener('submit', handleSubmit);
  document.addEventListener('product:variant-change', (e) => {
    syncCurrentVariantForms(e && e.detail && e.detail.variant);
  });
})();
